import type { Transaction, Auth } from 'oscar-typedef'
import WepinBackendAPI from '@/networks/api/wepin/WepinBackendAPI'
import {
  type ITransactionExecutor,
  type EncUVD,
  NearPrepareData,
  type NearSendExtraData,
  type AccountToken,
} from '@/types'

export class NearTokenTransactionExecutor implements ITransactionExecutor {
  private accountToken: AccountToken

  constructor(params: { accountToken: AccountToken }) {
    this.accountToken = params.accountToken
  }

  async getPrepareData(params: {
    to: string
    amount: string
  }): Promise<NearPrepareData> {
    const data =
      await WepinBackendAPI.getInstance().transaction.getTransactionPrepareData(
        {
          accountId: this.accountToken.accountId,
          contract: this.accountToken.coin?.contract ?? undefined,
          to: params.to,
          tokenId: this.accountToken.tokenId?.toString(),
          amount: params.amount,
        },
      )
    if (!data.detail) {
      throw new Error('No detail data for prepare')
    }
    return new NearPrepareData({
      nonce: data.detail.nonce,
      blockHash: data.detail.blockHash,
      fee: data.detail.fee,
      registered: data.detail.registered ?? 'completed',
    })
  }

  async sign(params: {
    to: string
    amount: string
    encUVD: EncUVD
    extraData: NearSendExtraData
    otpCode?: Auth.VerifyCode.Request.Body
  }): Promise<Transaction.Sign.Response.Body> {
    if (!this.accountToken.wallet) {
      throw new Error('No wallet for account')
    }
    const result = await WepinBackendAPI.getInstance().transaction.sign({
      type: 'transaction',
      walletId: this.accountToken.wallet.id,
      accountId: this.accountToken.accountId,
      pin: params.encUVD,
      contract: this.accountToken.coin?.contract,
      tokenId: this.accountToken.id,
      txData: {
        txType: 'token-transfer',
        to: params.to,
        from: this.accountToken.address,
        amount: params.amount,
        nonce: params.extraData.nonce,
        blockHash: params.extraData.blockHash,
        fee: params.extraData.fee,
        registered: params.extraData.registered,
      },
      otpCode: params.otpCode,
    })
    return result
  }

  async broadcast(params: {
    signResponse: Transaction.Sign.Response.Body
  }): Promise<Transaction.BroadcastTransaction.Response.Body> {
    if (!this.accountToken.wallet) {
      throw new Error('No wallet for account')
    }
    return await WepinBackendAPI.getInstance().transaction.broadcastTransaction(
      {
        walletId: this.accountToken.wallet.id,
        accountId: this.accountToken.accountId,
        contract: this.accountToken.coin?.contract,
        tokenId: this.accountToken.id.toString(),
        transaction: params.signResponse.transaction,
        signatureResult: params.signResponse.signatureResult,
      },
    )
  }
}
